/**
 * Token-swap impersonation — the ONLY way an admin sees the app as
 * someone else. The backend mints a real JWT for the target user, so
 * every screen and every API call sees that user's actual role and
 * scope. Nothing in the UI has to know a preview is happening; there
 * is deliberately no client-side "pretend role" overlay to keep in sync.
 *
 * Flow:
 *   1. POST `/api/auth/impersonate/{userId}` → `{ token }`
 *   2. Admin's token is parked under `adminToken`; the new token
 *      replaces `token`
 *   3. Hard reload so Redux and every cached query refetch as the target
 *
 * `endImpersonation` reverses the swap and returns the admin to where
 * they started.
 */

import { UserRole } from '../apis/api-client';
import { usersClient } from '../apis/client-instances';
import { getUserDisplayName } from './roleHelpers';
import { setViewAsLearner } from './viewAsLearnerPref';

const ADMIN_TOKEN_KEY = 'adminToken';
const ACTIVE_TOKEN_KEY = 'token';
const IMPERSONATED_NAME_KEY = 'impersonatedName';
const RETURN_PATH_KEY = 'impersonationReturnPath';
// The admin's workspace-switcher pin is sent as a request header; it
// must not ride along on the impersonated user's requests.
const WORKSPACE_PIN_KEY = 'viewAsWorkspaceId';

const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://localhost:44313';

/** True when an admin's original token is parked in localStorage. */
export function isImpersonating(): boolean {
  return !!localStorage.getItem(ADMIN_TOKEN_KEY);
}

export function getImpersonatedName(): string | null {
  return localStorage.getItem(IMPERSONATED_NAME_KEY);
}

/** Swap to the target user's token and reload into their view. Throws a
 *  human-readable message on failure so callers can snackbar it. */
export async function startImpersonation(userId: number, userName: string, returnPath: string): Promise<void> {
  const adminToken = localStorage.getItem(ACTIVE_TOKEN_KEY);
  if (!adminToken) throw new Error('Not signed in.');
  if (isImpersonating()) throw new Error('Already impersonating — exit the current session first.');

  const response = await fetch(`${baseUrl}/api/auth/impersonate/${userId}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${adminToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (response.status === 403) {
    throw new Error('You don\'t have permission to impersonate this user.');
  }
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Impersonation failed (HTTP ${response.status}).`);
  }

  const body = await response.json().catch(() => null);
  const newToken: string | undefined = body?.token;
  if (!newToken) {
    throw new Error('Server didn\'t return an impersonation token.');
  }

  localStorage.setItem(ADMIN_TOKEN_KEY, adminToken);
  localStorage.setItem(ACTIVE_TOKEN_KEY, newToken);
  localStorage.setItem(IMPERSONATED_NAME_KEY, userName);
  localStorage.setItem(RETURN_PATH_KEY, returnPath);
  localStorage.removeItem(WORKSPACE_PIN_KEY);
  setViewAsLearner(false);

  window.location.assign('/courses');
}

/** Impersonate a Workspace Admin of the given workspace. Throws if the
 *  workspace has no active Workspace Admin to impersonate. */
export async function impersonateWorkspaceAdmin(workspaceId: number, returnPath: string): Promise<void> {
  const result = await usersClient.searchUsersPaged(workspaceId, UserRole.WorkspaceAdmin, 'active', undefined, 1, 1);
  const admin = result?.items?.[0];
  if (!admin?.id) {
    throw new Error('This workspace has no active Workspace Admin to impersonate.');
  }
  await startImpersonation(admin.id, getUserDisplayName(admin), returnPath);
}

/** Restore the admin's token and reload back to where impersonation
 *  started. Safe to call when not impersonating. */
export function endImpersonation(): void {
  const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
  const returnPath = localStorage.getItem(RETURN_PATH_KEY) || '/courses';
  if (adminToken) localStorage.setItem(ACTIVE_TOKEN_KEY, adminToken);
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(IMPERSONATED_NAME_KEY);
  localStorage.removeItem(RETURN_PATH_KEY);
  localStorage.removeItem(WORKSPACE_PIN_KEY);
  setViewAsLearner(false);
  window.location.assign(returnPath);
}
